import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Modal } from '@sorare/core/src/atoms/Modal';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { AnimatedDiamond } from '@sorare/core/src/components/rivals/branded/AnimatedDiamond';
import { navLabels } from '@sorare/core/src/lib/glossary';
import {
  Color,
  HexColor,
  HexColorOnly,
  RGBColor,
} from '@sorare/core/src/style/types';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';
import { BackgroundAligned } from '@sorare/gameplay/src/components/rivals/GridBackground/BackgroundAligned';

import { BrandedInfo } from 'components/stories/rivals/leaderboards/BrandedInfo';

const Root = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: var(--c-neutral-100);
`;
const Foreground = styled(Vertical)`
  position: absolute;
  inset: 0;
  height: 100%;
  padding: var(--double-unit);
`;
const Content = styled.div`
  flex: 1;
`;
const FullWidthBrandedInfo = styled(BrandedInfo)`
  margin: auto;
`;
const Blur = styled.div`
  backdrop-filter: blur(10px);
  clip-path: polygon(50% 0, 100% 50%, 50% 100%, 0 50%);
`;

type Props = {
  title?: ReactNode;
  subtitle?: ReactNode;
  content?: ReactNode;
  footer?: { color: HexColor; content: ReactNode };
  cta?: ReactNode;
  diamondColor: RGBColor;
  gridColor: HexColorOnly;
  titleColor: Color;
  secondDiamonWrapper?: boolean;
};
export const BaseLayout = ({
  title,
  subtitle,
  content,
  footer,
  cta,
  diamondColor,
  gridColor,
  titleColor,
  secondDiamonWrapper,
}: Props) => {
  return (
    <Modal>
      <Root>
        <GridBackground height="100%" color={gridColor}>
          <BackgroundAligned horizontalDelta={0} verticalDelta={-2}>
            <Blur>
              <AnimatedDiamond
                fill={diamondColor}
                width={400}
                height={400}
                opacity={0.15}
              >
                {secondDiamonWrapper ? (
                  <AnimatedDiamond fill={diamondColor} width={320} height={320}>
                    {content}
                  </AnimatedDiamond>
                ) : (
                  content
                )}
              </AnimatedDiamond>
            </Blur>
          </BackgroundAligned>
        </GridBackground>
        <Foreground>
          <Vertical gap={0} center>
            <LabelS color={titleColor} uppercase bold>
              <FormattedMessage {...navLabels.mySquad} />
            </LabelS>
            {title}
            {subtitle}
          </Vertical>
          <Content />
          <Vertical center gap={2}>
            {footer && (
              <FullWidthBrandedInfo fillColor={footer?.color} fillOpacity={1}>
                {footer?.content}
              </FullWidthBrandedInfo>
            )}
            {cta}
          </Vertical>
        </Foreground>
      </Root>
    </Modal>
  );
};
